import { useState, useEffect, useCallback, useRef } from "react";
import {
  Play,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Info,
  Wand2,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  RefreshCw,
  Code2,
  BarChart3,
  Shield,
  Sparkles,
  Search,
  TrendingUp,
  Target,
  Users,
  CheckCircle,
  MinusCircle,
} from "lucide-react";
import * as api from "../lib/api";

const PRIMARY = "#018D4C";

const SEVERITY_META = {
  critical: { color: "#DC2626", bg: "#FEF2F2", label: "Critical", Icon: XCircle },
  warning:  { color: "#F59E0B", bg: "#FFFBEB", label: "Warning",  Icon: AlertTriangle },
  suggestion: { color: "#2563EB", bg: "#EFF6FF", label: "Suggestion", Icon: Info },
};

const ELIGIBILITY_META = {
  eligible:   { color: "#16A34A", bg: "#F0FDF4", label: "Eligible" },
  partial:    { color: "#D97706", bg: "#FFFBEB", label: "Partial" },
  ineligible: { color: "#DC2626", bg: "#FEF2F2", label: "Ineligible" },
};

function HealthGauge({ score }) {
  const color = score >= 75 ? "#16A34A" : score >= 50 ? "#D97706" : "#DC2626";
  const label = score >= 75 ? "Good" : score >= 50 ? "Needs Work" : "Poor";
  return (
    <div className="flex flex-col items-center gap-1">
      <div
        className="w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold text-white shadow"
        style={{ background: color }}
      >
        {score}
      </div>
      <span className="text-xs font-medium" style={{ color }}>{label}</span>
    </div>
  );
}

function SeverityBadge({ severity }) {
  const meta = SEVERITY_META[severity] || SEVERITY_META.suggestion;
  const { Icon } = meta;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{ background: meta.bg, color: meta.color }}
    >
      <Icon size={11} />
      {meta.label}
    </span>
  );
}

function EligibilityBadge({ status }) {
  const meta = ELIGIBILITY_META[status] || ELIGIBILITY_META.ineligible;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{ background: meta.bg, color: meta.color }}
    >
      {meta.label}
    </span>
  );
}

function IssueRow({ issue }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
        onClick={() => setOpen(o => !o)}
      >
        <SeverityBadge severity={issue.severity} />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-800">{issue.message}</p>
          {issue.schema_type && (
            <span className="text-xs text-gray-400 font-mono">{issue.schema_type}</span>
          )}
        </div>
        {open ? <ChevronUp size={14} className="text-gray-400 mt-0.5 shrink-0" /> : <ChevronDown size={14} className="text-gray-400 mt-0.5 shrink-0" />}
      </button>
      {open && (
        <div className="px-4 pb-3 ml-[72px]">
          <p className="text-sm text-gray-600">
            <span className="font-medium text-gray-700">Fix: </span>{issue.fix}
          </p>
          {issue.field && (
            <p className="text-xs text-gray-400 mt-1 font-mono">Missing field: {issue.field}</p>
          )}
        </div>
      )}
    </div>
  );
}

function UrlRow({ row }) {
  const [open, setOpen] = useState(false);
  const critCount = (row.issues || []).filter(i => i.severity === "critical").length;
  const warnCount = (row.issues || []).filter(i => i.severity === "warning").length;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden mb-2">
      <button
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
        onClick={() => setOpen(o => !o)}
      >
        <HealthGauge score={row.health_score} />
        <div className="flex-1 min-w-0">
          <a
            href={row.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-blue-600 hover:underline flex items-center gap-1 truncate"
            onClick={e => e.stopPropagation()}
          >
            <span className="truncate">{row.url}</span>
            <ExternalLink size={11} className="shrink-0" />
          </a>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {row.schema_types && row.schema_types.length > 0
              ? row.schema_types.map(t => (
                  <span key={t} className="text-xs px-1.5 py-0.5 bg-green-50 text-green-700 rounded font-mono">{t}</span>
                ))
              : <span className="text-xs text-gray-400">No schema detected</span>
            }
          </div>
          <div className="flex items-center gap-3 mt-1">
            {critCount > 0 && <span className="text-xs text-red-600 font-medium">{critCount} critical</span>}
            {warnCount > 0 && <span className="text-xs text-amber-600 font-medium">{warnCount} warnings</span>}
            {critCount === 0 && warnCount === 0 && row.has_schema && (
              <span className="text-xs text-green-600 font-medium">No major issues</span>
            )}
          </div>
        </div>
        {/* Rich result eligibility */}
        <div className="flex flex-col gap-1 items-end mr-2">
          {Object.entries(row.rich_result_eligibility || {}).slice(0, 3).map(([type, status]) => (
            <div key={type} className="flex items-center gap-1.5">
              <span className="text-xs text-gray-500">{type}</span>
              <EligibilityBadge status={status} />
            </div>
          ))}
        </div>
        {open ? <ChevronUp size={14} className="text-gray-400 shrink-0" /> : <ChevronDown size={14} className="text-gray-400 shrink-0" />}
      </button>

      {open && (
        <div className="border-t border-gray-100">
          {/* Issues */}
          {(row.issues || []).length > 0 ? (
            <div>
              <p className="text-xs font-semibold text-gray-500 px-4 py-2 bg-gray-50">
                ISSUES ({row.issues.length})
              </p>
              {row.issues.map((issue, i) => <IssueRow key={i} issue={issue} />)}
            </div>
          ) : (
            <p className="text-sm text-gray-500 px-4 py-3">No issues found.</p>
          )}

          {/* AI Recommendations */}
          {(row.ai_recommendations || []).length > 0 && (
            <div className="border-t border-gray-100">
              <p className="text-xs font-semibold text-gray-500 px-4 py-2 bg-purple-50 flex items-center gap-1">
                <Sparkles size={11} className="text-purple-500" />
                AI RECOMMENDATIONS
              </p>
              {row.ai_recommendations.map((rec, i) => (
                <div key={i} className="px-4 py-3 border-b border-gray-100 last:border-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold text-purple-600">#{rec.priority || i + 1}</span>
                    <p className="text-sm font-medium text-gray-800">{rec.action}</p>
                  </div>
                  {rec.impact && <p className="text-xs text-gray-500 ml-5">{rec.impact}</p>}
                  {rec.json_ld_snippet && (
                    <pre className="mt-2 ml-5 text-xs bg-gray-900 text-green-400 p-2 rounded overflow-x-auto">
                      {typeof rec.json_ld_snippet === "string"
                        ? rec.json_ld_snippet
                        : JSON.stringify(rec.json_ld_snippet, null, 2)}
                    </pre>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Competitor Analysis Tab ───────────────────────────────────────────────────

const OPPORTUNITY_COLOR = score =>
  score >= 7 ? "#DC2626" : score >= 4 ? "#D97706" : "#2563EB";

function OpportunityScore({ score }) {
  const color = OPPORTUNITY_COLOR(score);
  return (
    <span className="inline-flex items-center gap-1 text-xs font-bold" style={{ color }}>
      <Target size={11} />
      {score}/10
    </span>
  );
}

function GapRow({ row }) {
  const hasIt = row.user_has_any;
  return (
    <tr className="border-b border-gray-50 hover:bg-gray-50">
      <td className="py-2.5 px-3 font-mono text-xs text-gray-800">{row.schema_type}</td>
      <td className="py-2.5 px-3 text-center">
        <div className="flex flex-col items-center">
          <span className="text-sm font-bold text-gray-800">{row.competitor_count}/{row.total_competitors}</span>
          <div className="w-16 h-1.5 bg-gray-100 rounded-full mt-1">
            <div
              className="h-1.5 rounded-full"
              style={{ width: `${row.competitor_pct}%`, background: PRIMARY }}
            />
          </div>
          <span className="text-xs text-gray-400">{row.competitor_pct}%</span>
        </div>
      </td>
      <td className="py-2.5 px-3 text-center">
        {hasIt ? (
          <span className="inline-flex items-center gap-1 text-xs text-green-600 font-medium">
            <CheckCircle size={12} /> Yes
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs text-red-500 font-medium">
            <MinusCircle size={12} /> No
          </span>
        )}
      </td>
      <td className="py-2.5 px-3 text-center">
        <OpportunityScore score={row.opportunity_score} />
      </td>
    </tr>
  );
}

function CompetitorRow({ comp }) {
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-gray-100 last:border-0">
      <div className={`mt-0.5 w-2 h-2 rounded-full shrink-0 ${comp.has_schema ? "bg-green-400" : "bg-gray-300"}`} />
      <div className="flex-1 min-w-0">
        <a
          href={comp.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-blue-600 hover:underline flex items-center gap-1 truncate"
        >
          <span className="truncate">{comp.url}</span>
          <ExternalLink size={10} className="shrink-0" />
        </a>
        <div className="flex flex-wrap gap-1 mt-1">
          {comp.schema_types && comp.schema_types.length > 0
            ? comp.schema_types.map(t => (
                <span key={t} className="text-xs px-1.5 py-0.5 bg-green-50 text-green-700 rounded font-mono">{t}</span>
              ))
            : <span className="text-xs text-gray-400">No schema found</span>
          }
        </div>
      </div>
    </div>
  );
}

function CompetitorAnalysisTab() {
  const [keyword, setKeyword] = useState("");
  const [userUrl, setUserUrl] = useState("");
  const [numComp, setNumComp] = useState(10);
  const [running, setRunning] = useState(false);
  const [status, setStatus] = useState({ status: "idle" });
  const [results, setResults] = useState([]);
  const [activeResult, setActiveResult] = useState(null);
  const pollRef = useRef(null);

  const fetchResults = useCallback(async () => {
    try {
      const res = await api.getCompeteResults();
      const rows = res.results || [];
      setResults(rows);
      if (rows.length > 0 && !activeResult) setActiveResult(rows[0]);
    } catch {}
  }, [activeResult]);

  const pollStatus = useCallback(async () => {
    try {
      const s = await api.getCompeteStatus();
      setStatus(s);
      if (s.status === "completed" || s.status === "error") {
        clearInterval(pollRef.current);
        setRunning(false);
        await fetchResults();
      }
    } catch {
      clearInterval(pollRef.current);
      setRunning(false);
    }
  }, [fetchResults]);

  useEffect(() => {
    fetchResults();
    api.getCompeteStatus().then(s => {
      setStatus(s);
      if (s.status === "running") {
        setRunning(true);
        pollRef.current = setInterval(pollStatus, 3000);
      }
    }).catch(() => {});
    return () => clearInterval(pollRef.current);
  }, [fetchResults, pollStatus]);

  const handleAnalyze = async () => {
    if (!keyword.trim()) return;
    setRunning(true);
    setStatus({ status: "running", keyword: keyword.trim() });
    try {
      await api.runCompetitorAnalysis({
        keyword: keyword.trim(),
        user_url: userUrl.trim(),
        num_competitors: numComp,
      });
      pollRef.current = setInterval(pollStatus, 3000);
    } catch (e) {
      setRunning(false);
      alert("Failed to start: " + (e?.response?.data?.detail || e.message));
    }
  };

  const result = activeResult;

  return (
    <div className="space-y-5">
      {/* Search input */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h2 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5">
          <Users size={14} style={{ color: PRIMARY }} />
          Analyze Competitors
        </h2>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={keyword}
            onChange={e => setKeyword(e.target.value)}
            placeholder="Target keyword (e.g. best running shoes)"
            className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            onKeyDown={e => e.key === "Enter" && handleAnalyze()}
          />
          <input
            type="url"
            value={userUrl}
            onChange={e => setUserUrl(e.target.value)}
            placeholder="Your URL (optional)"
            className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
          />
          <select
            value={numComp}
            onChange={e => setNumComp(Number(e.target.value))}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none"
          >
            {[5, 10, 15, 20].map(n => (
              <option key={n} value={n}>Top {n}</option>
            ))}
          </select>
          <button
            onClick={handleAnalyze}
            disabled={running || !keyword.trim()}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60 shrink-0"
            style={{ background: PRIMARY }}
          >
            {running ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
            {running ? `Fetching… (${status.fetched || 0}/${status.total || "?"})` : "Analyze"}
          </button>
        </div>
        {status.status === "error" && (
          <p className="mt-2 text-xs text-red-500">{status.error}</p>
        )}
      </div>

      {/* Past results selector */}
      {results.length > 1 && (
        <div className="flex gap-2 flex-wrap">
          {results.map(r => (
            <button
              key={r.id}
              onClick={() => setActiveResult(r)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                activeResult?.id === r.id
                  ? "text-white border-transparent"
                  : "text-gray-500 border-gray-200 hover:bg-gray-50"
              }`}
              style={activeResult?.id === r.id ? { background: PRIMARY } : {}}
            >
              {r.keyword}
            </button>
          ))}
        </div>
      )}

      {result && (
        <>
          {/* Top opportunities */}
          {(result.opportunities || []).length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h2 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5">
                <Target size={14} className="text-red-500" />
                Top Opportunities for "{result.keyword}"
              </h2>
              <div className="space-y-2">
                {result.opportunities.map((opp, i) => (
                  <div
                    key={opp.schema_type}
                    className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 bg-gray-50"
                  >
                    <span
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
                      style={{ background: OPPORTUNITY_COLOR(opp.opportunity_score) }}
                    >
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 font-mono">{opp.schema_type}</p>
                      <p className="text-xs text-gray-500">
                        Used by {opp.competitor_count}/{opp.total_competitors} competitors ({opp.competitor_pct}%)
                        — you don't have it
                      </p>
                    </div>
                    <OpportunityScore score={opp.opportunity_score} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Gap analysis table */}
          {(result.gap_analysis || []).length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="flex items-center gap-1.5 px-4 py-3 border-b border-gray-100">
                <BarChart3 size={14} style={{ color: PRIMARY }} />
                <span className="text-sm font-semibold text-gray-700">Schema Gap Analysis</span>
                <span className="text-xs text-gray-400 ml-1">vs {result.total_competitors} competitors</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-xs text-gray-400 border-b border-gray-100 bg-gray-50">
                      <th className="text-left px-3 py-2 font-medium">Schema Type</th>
                      <th className="text-center px-3 py-2 font-medium">Competitor Adoption</th>
                      <th className="text-center px-3 py-2 font-medium">You Have It</th>
                      <th className="text-center px-3 py-2 font-medium">Opportunity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.gap_analysis.map(row => (
                      <GapRow key={row.schema_type} row={row} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Competitor URLs */}
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <div className="flex items-center gap-1.5">
                <TrendingUp size={14} style={{ color: PRIMARY }} />
                <span className="text-sm font-semibold text-gray-700">
                  Competitor Pages ({result.total_competitors})
                </span>
              </div>
              <span className="text-xs text-gray-400">via {result.search_provider}</span>
            </div>
            <div className="p-4">
              {(result.competitor_data || []).map((comp, i) => (
                <CompetitorRow key={i} comp={comp} />
              ))}
            </div>
          </div>
        </>
      )}

      {!result && !running && (
        <div className="text-center py-16 text-gray-400">
          <Users size={32} className="mx-auto mb-3 text-gray-200" />
          <p className="text-sm">Enter a keyword above to analyze competitor schema strategies.</p>
        </div>
      )}
    </div>
  );
}

export default function SchemaAuditPage() {
  const [activeTab, setActiveTab] = useState("audit"); // audit | competitor
  const [summary, setSummary] = useState(null);
  const [results, setResults] = useState([]);
  const [status, setStatus] = useState({ status: "idle" });
  const [loading, setLoading] = useState(true);
  const [enriching, setEnriching] = useState(false);
  const [filter, setFilter] = useState("all"); // all | critical | warning | no_schema
  const pollRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [sum, res] = await Promise.all([
        api.getSchemaSummary(),
        api.getSchemaResults({ limit: 200 }),
      ]);
      setSummary(sum);
      setResults(res.results || []);
    } catch {
      // ignore — no data yet
    } finally {
      setLoading(false);
    }
  }, []);

  const pollStatus = useCallback(async () => {
    try {
      const s = await api.getSchemaStatus();
      setStatus(s);
      if (s.status === "completed" || s.status === "error") {
        clearInterval(pollRef.current);
        await fetchData();
      }
    } catch {
      clearInterval(pollRef.current);
    }
  }, [fetchData]);

  useEffect(() => {
    fetchData();
    api.getSchemaStatus().then(s => {
      setStatus(s);
      if (s.status === "running") {
        pollRef.current = setInterval(pollStatus, 3000);
      }
    }).catch(() => {});
    return () => clearInterval(pollRef.current);
  }, [fetchData, pollStatus]);

  const handleRun = async () => {
    try {
      await api.runSchemaAudit();
      setStatus({ status: "running" });
      pollRef.current = setInterval(pollStatus, 3000);
    } catch (e) {
      alert("Failed to start audit: " + (e?.response?.data?.detail || e.message));
    }
  };

  const handleEnrich = async () => {
    setEnriching(true);
    try {
      await api.enrichSchemaWithAI();
      setTimeout(() => {
        fetchData();
        setEnriching(false);
      }, 8000);
    } catch {
      setEnriching(false);
    }
  };

  const running = status.status === "running";

  const filteredResults = results.filter(r => {
    if (filter === "critical") return (r.issues || []).some(i => i.severity === "critical");
    if (filter === "warning") return (r.issues || []).some(i => i.severity === "warning");
    if (filter === "no_schema") return !r.has_schema;
    return true;
  });

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield size={22} style={{ color: PRIMARY }} />
            Schema Intelligence
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Structured data audit — detect issues, validate rich results, analyze competitors
          </p>
        </div>
        {activeTab === "audit" && (
          <div className="flex items-center gap-2">
            {results.length > 0 && (
              <button
                onClick={handleEnrich}
                disabled={enriching || running}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border border-purple-200 text-purple-700 hover:bg-purple-50 disabled:opacity-50 transition-colors"
              >
                {enriching ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                AI Recs
              </button>
            )}
            <button
              onClick={handleRun}
              disabled={running}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60 transition-colors"
              style={{ background: PRIMARY }}
            >
              {running ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
              {running ? `Auditing… (${status.audited || 0}/${status.total || "?"})` : "Run Audit"}
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-200">
        {[
          { key: "audit", label: "Audit", Icon: Shield },
          { key: "competitor", label: "Competitor Analysis", Icon: Users },
        ].map(({ key, label, Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === key
                ? "border-green-600 text-green-700"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <Icon size={14} />
            {label}
          </button>
        ))}
      </div>

      {activeTab === "competitor" && <CompetitorAnalysisTab />}
      {activeTab === "audit" && (<>

      {/* Summary Cards */}
      {summary && summary.total_pages > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-1">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Health Score</span>
            <div className="flex items-center gap-2">
              <span className="text-3xl font-bold" style={{
                color: summary.avg_health_score >= 75 ? "#16A34A" : summary.avg_health_score >= 50 ? "#D97706" : "#DC2626"
              }}>
                {summary.avg_health_score}
              </span>
              <span className="text-sm text-gray-400">/100</span>
            </div>
            <span className="text-xs text-gray-500">{summary.total_pages} pages audited</span>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-1">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Schema Coverage</span>
            <span className="text-3xl font-bold text-gray-800">
              {summary.total_pages > 0 ? Math.round((summary.pages_with_schema / summary.total_pages) * 100) : 0}%
            </span>
            <span className="text-xs text-gray-500">
              {summary.pages_with_schema} with schema · {summary.pages_without_schema} without
            </span>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-1">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Issues</span>
            <div className="flex items-center gap-3">
              <span className="text-2xl font-bold text-red-600">{summary.critical_count}</span>
              <span className="text-sm text-gray-400">critical</span>
            </div>
            <span className="text-xs text-gray-500">
              {summary.warning_count} warnings · {summary.suggestion_count} suggestions
            </span>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-1">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Schema Types</span>
            <div className="flex flex-wrap gap-1 mt-1">
              {Object.entries(summary.schema_type_distribution || {}).slice(0, 5).map(([type, count]) => (
                <span key={type} className="text-xs px-1.5 py-0.5 bg-green-50 text-green-700 rounded font-mono">
                  {type} ({count})
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Rich Result Eligibility Summary */}
      {summary && Object.keys(summary.rich_result_eligibility_summary || {}).length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <h2 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5">
            <BarChart3 size={14} style={{ color: PRIMARY }} />
            Rich Result Eligibility
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-400 border-b border-gray-100">
                  <th className="text-left pb-2 font-medium">Schema Type</th>
                  <th className="text-center pb-2 font-medium text-green-600">Eligible</th>
                  <th className="text-center pb-2 font-medium text-amber-600">Partial</th>
                  <th className="text-center pb-2 font-medium text-red-600">Ineligible</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(summary.rich_result_eligibility_summary).map(([type, counts]) => (
                  <tr key={type} className="border-b border-gray-50">
                    <td className="py-2 font-mono text-xs text-gray-700">{type}</td>
                    <td className="text-center py-2 text-green-600 font-medium">{counts.eligible || 0}</td>
                    <td className="text-center py-2 text-amber-600 font-medium">{counts.partial || 0}</td>
                    <td className="text-center py-2 text-red-600 font-medium">{counts.ineligible || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Results */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
            <Code2 size={14} style={{ color: PRIMARY }} />
            Pages ({filteredResults.length})
          </h2>
          <div className="flex items-center gap-1">
            {[
              { key: "all", label: "All" },
              { key: "critical", label: "Critical" },
              { key: "warning", label: "Warnings" },
              { key: "no_schema", label: "No Schema" },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`text-xs px-2.5 py-1 rounded-full transition-colors ${
                  filter === key
                    ? "text-white font-semibold"
                    : "text-gray-500 hover:bg-gray-100"
                }`}
                style={filter === key ? { background: PRIMARY } : {}}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-400">
              <Loader2 size={20} className="animate-spin mr-2" />
              Loading…
            </div>
          ) : filteredResults.length === 0 ? (
            <div className="text-center py-12">
              <Shield size={32} className="mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 text-sm">
                {results.length === 0
                  ? 'No schema audit data yet. Click "Run Audit" to scan your pages.'
                  : "No pages match this filter."}
              </p>
            </div>
          ) : (
            filteredResults.map(row => <UrlRow key={row.id} row={row} />)
          )}
        </div>
      </div>
      </>)}
    </div>
  );
}
